import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createDoc, logActivity } from "../../lib/store";

const inputCls =
  "w-full rounded-xl border border-[#0033FF]/12 bg-white px-4 py-3 text-[#0b1221] placeholder:text-[#9aa7bd] focus:outline-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent transition-shadow duration-200";

const empty = {
  fullName: "",
  mobileNumber: "",
  email: "",
  reasonForDeletion: "",
};

export const DeleteAccount = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    
    if (!form.fullName || !form.mobileNumber || !form.email) {
      toast.error("Please fill all required fields.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(form.mobileNumber.replace(/\D/g, ""))) {
      toast.error("Please enter a valid Indian mobile number.");
      return;
    }

    setLoading(true);
    try {
      await createDoc("account_deletions", {
        ...form,
        status: "pending",
        requestedAt: new Date().toISOString(),
      });
      await logActivity("account_deletion", `Account deletion request from ${form.fullName}`);
      setForm(empty);
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting deletion request:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7fb] py-20">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-[#0033FF] hover:text-[#0026bf] font-medium mb-8 transition-colors duration-200"
          data-testid="delete-account-back"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        {!submitted ? (
          <>
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <h1 className="font-display font-extrabold tracking-tighter text-4xl md:text-5xl text-[#0b1221] mb-4">
                Delete Your QuickDrop Go Account
              </h1>
              <p className="text-lg text-[#5e6c84] max-w-2xl">
                We understand you may want to delete your QuickDrop Go account and associated personal data. 
                We are committed to respecting your privacy and handling your request securely.
              </p>
            </motion.div>

            {/* Main Content */}
            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              {/* Information Column */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="lg:col-span-1 space-y-6"
              >
                {/* Info Card 1 */}
                <div className="bg-white rounded-2xl p-6 border border-[#0033FF]/8 shadow-[0_4px_12px_rgba(0,51,255,0.04)]">
                  <div className="flex items-start gap-4 mb-3">
                    <CheckCircle className="w-6 h-6 text-[#0033FF] shrink-0 mt-1" />
                    <h3 className="font-semibold text-[#0b1221]">Request Review</h3>
                  </div>
                  <p className="text-sm text-[#5e6c84] leading-relaxed">
                    Your account deletion request will be reviewed by our team within 3-5 business days.
                  </p>
                </div>

                {/* Info Card 2 */}
                <div className="bg-white rounded-2xl p-6 border border-[#0033FF]/8 shadow-[0_4px_12px_rgba(0,51,255,0.04)]">
                  <div className="flex items-start gap-4 mb-3">
                    <CheckCircle className="w-6 h-6 text-[#0033FF] shrink-0 mt-1" />
                    <h3 className="font-semibold text-[#0b1221]">Data Deletion</h3>
                  </div>
                  <p className="text-sm text-[#5e6c84] leading-relaxed">
                    Personal account data will be deleted after verification and processing of your request.
                  </p>
                </div>

                {/* Info Card 3 */}
                <div className="bg-white rounded-2xl p-6 border border-[#0033FF]/8 shadow-[0_4px_12px_rgba(0,51,255,0.04)]">
                  <div className="flex items-start gap-4 mb-3">
                    <AlertCircle className="w-6 h-6 text-amber-500 shrink-0 mt-1" />
                    <h3 className="font-semibold text-[#0b1221]">Retained Data</h3>
                  </div>
                  <p className="text-sm text-[#5e6c84] leading-relaxed">
                    Some transaction and order information may be retained when legally or operationally required.
                  </p>
                </div>

                {/* Support Card */}
                <div className="bg-blue-50 rounded-2xl p-6 border border-[#0033FF]/12">
                  <h3 className="font-semibold text-[#0b1221] mb-2">Need Help?</h3>
                  <p className="text-sm text-[#5e6c84] mb-3">
                    Contact our support team for any assistance with account deletion.
                  </p>
                  <a
                    href="mailto:quickdrop1999@gmail.com"
                    className="inline-block text-sm font-medium text-[#0033FF] hover:text-[#0026bf] transition-colors"
                  >
                    quickdrop1999@gmail.com
                  </a>
                </div>
              </motion.div>

              {/* Form Column */}
              <motion.form
                onSubmit={submit}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="lg:col-span-2 bg-white rounded-3xl p-8 md:p-10 border border-[#0033FF]/8 shadow-[0_20px_60px_rgba(0,51,255,0.08)]"
                data-testid="delete-account-form"
              >
                <h2 className="text-2xl font-bold text-[#0b1221] mb-6">
                  Request Account Deletion
                </h2>

                {/* Full Name */}
                <label className="block mb-5">
                  <span className="block text-sm font-medium text-[#0b1221] mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    className={inputCls}
                    value={form.fullName}
                    onChange={set("fullName")}
                    placeholder="Enter your full name"
                    data-testid="delete-account-name"
                  />
                </label>

                {/* Mobile Number */}
                <label className="block mb-5">
                  <span className="block text-sm font-medium text-[#0b1221] mb-2">
                    Registered Mobile Number <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="tel"
                    className={inputCls}
                    value={form.mobileNumber}
                    onChange={set("mobileNumber")}
                    placeholder="+91 98765 43210"
                    data-testid="delete-account-mobile"
                  />
                  <p className="text-xs text-[#9aa7bd] mt-1">
                    Enter the mobile number registered with your QuickDrop Go account
                  </p>
                </label>

                {/* Email Address */}
                <label className="block mb-5">
                  <span className="block text-sm font-medium text-[#0b1221] mb-2">
                    Registered Email Address <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@email.com"
                    data-testid="delete-account-email"
                  />
                  <p className="text-xs text-[#9aa7bd] mt-1">
                    Enter the email address registered with your QuickDrop Go account
                  </p>
                </label>

                {/* Reason for Deletion */}
                <label className="block mb-6">
                  <span className="block text-sm font-medium text-[#0b1221] mb-2">
                    Reason for Deletion
                  </span>
                  <textarea
                    rows={4}
                    className={inputCls + " resize-none"}
                    value={form.reasonForDeletion}
                    onChange={set("reasonForDeletion")}
                    placeholder="Tell us why you'd like to delete your account (optional)"
                    data-testid="delete-account-reason"
                  />
                </label>

                {/* Consent */}
                <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-[#0033FF]/12">
                  <p className="text-sm text-[#5e6c84]">
                    <span className="font-medium text-[#0b1221]">Please note:</span> By submitting this request, you confirm that you want to delete your QuickDrop Go account and associated personal data. This action cannot be immediately undone and will be processed by our team after verification.
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] text-white font-semibold py-4 rounded-xl hover:bg-[#0026bf] transition-colors duration-200 disabled:opacity-70"
                  data-testid="delete-account-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting Request...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Request Account Deletion
                    </>
                  )}
                </button>
              </motion.form>
            </div>

            {/* Additional Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 border border-[#0033FF]/8 shadow-[0_4px_12px_rgba(0,51,255,0.04)]"
            >
              <h3 className="text-lg font-bold text-[#0b1221] mb-4">
                Account Deletion Process
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-[#0b1221] mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0033FF] text-white text-xs font-bold">1</span>
                    Submit Your Request
                  </h4>
                  <p className="text-sm text-[#5e6c84]">
                    Fill out the form above with your account details and submit your deletion request.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0b1221] mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0033FF] text-white text-xs font-bold">2</span>
                    Verification
                  </h4>
                  <p className="text-sm text-[#5e6c84]">
                    Our team will verify your identity and request within 3-5 business days.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0b1221] mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0033FF] text-white text-xs font-bold">3</span>
                    Data Deletion
                  </h4>
                  <p className="text-sm text-[#5e6c84]">
                    Once verified, your personal account data will be securely deleted from our systems.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0b1221] mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0033FF] text-white text-xs font-bold">4</span>
                    Confirmation
                  </h4>
                  <p className="text-sm text-[#5e6c84]">
                    You will receive a confirmation email once your account has been successfully deleted.
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        ) : (
          /* Success Message */
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-12 border border-green-200 shadow-[0_20px_60px_rgba(34,197,94,0.08)] text-center max-w-2xl mx-auto"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-[#0b1221] mb-4">
              Request Received!
            </h2>
            <p className="text-lg text-[#5e6c84] mb-6">
              Your account deletion request has been successfully submitted. Our team will review and process it within 3-5 business days.
            </p>
            <div className="bg-blue-50 rounded-2xl p-6 mb-8 border border-[#0033FF]/12 text-left">
              <h3 className="font-semibold text-[#0b1221] mb-3">What happens next?</h3>
              <ul className="space-y-2 text-sm text-[#5e6c84]">
                <li className="flex items-start gap-3">
                  <span className="text-[#0033FF] font-bold">•</span>
                  <span>We will verify your identity and account information</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#0033FF] font-bold">•</span>
                  <span>Your personal data will be securely deleted</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#0033FF] font-bold">•</span>
                  <span>You will receive a confirmation email once completed</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#0033FF] font-bold">•</span>
                  <span>Some transaction data may be retained as required by law</span>
                </li>
              </ul>
            </div>
            <p className="text-sm text-[#5e6c84] mb-8">
              If you have any questions, please contact us at{" "}
              <a href="mailto:quickdrop1999@gmail.com" className="text-[#0033FF] hover:text-[#0026bf] font-medium">
                quickdrop1999@gmail.com
              </a>
            </p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 bg-[#0033FF] text-white font-semibold px-8 py-3 rounded-xl hover:bg-[#0026bf] transition-colors duration-200"
              data-testid="delete-account-success-home"
            >
              Back to Home
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};
